// The Overview, where everyone lands: the organization's AI at a glance over
// the range, against the span before it (lib/overview.js). Three tiles
// (traces, agents, and traces whose worst risk was high or critical) over the
// share of the traces each provider took; the agents table beside the
// severity rows and the investigation cards; then where the numbers come
// from. Each block paints when its own read lands (lib/overviewReads.js),
// says Unavailable where it failed, and names each mark in words on hover or
// focus. A row leads to its page: an agent to Fleet, a severity to Findings,
// a card to the investigations. Export report prints the page as one
// landscape page. It never scrolls: the chart takes what the tables leave of
// the window (lib/overview.js fitRoom).
import { ArrowDownTrayIcon } from "@heroicons/react/24/outline";
import Button from "../components/ui/Button.jsx";
import { Fragment, useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { useSearchParams } from "react-router-dom";
import DateRangePicker, { lengthOf } from "../components/DateRangePicker.jsx";
import { AgentIcon } from "../components/Dimension.jsx";
import HoverTooltip from "../components/HoverTooltip.jsx";
import { useElementSize } from "../components/kernelPrimitives/shared.jsx";
import { useQuery } from "../lib/dataClient.js";
import { formatAgo } from "../lib/escalationLabels.js";
import { setConsole } from "../lib/findingsConsoleStore.js";
import { setFleet } from "../lib/fleetConsoleStore.js";
import { providerName } from "../lib/fleetBreakdown.js";
import {
  LEVELS, LEVEL_LABEL, SHARE_WINDOW, TOP, agentCounts, agentShares, agentsOf, agentsTable, fitRoom, fmt, investigationsIn, moveText, numberer, pctText, severityIn, shareSpan, tracesIn, windowsOfRange,
} from "../lib/overview.js";
import { hasPermission, useMyPermissions } from "../lib/permissions.js";
import { PERMISSIONS } from "../lib/permissionKeys.js";
import { spanWords } from "../lib/shell.js";
import { parseServerTimestamp } from "../lib/traceUtils.js";
import { useConsoleInvestigations } from "../lib/useConsoleInvestigations.js";
import { useCurrentUser } from "../lib/userInfo.js";
import "./OverviewPage.css";

const HOUR = 3_600_000;
const ON_DAY = new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" });
const ON_WEEKDAY = new Intl.DateTimeFormat(undefined, { weekday: "long" });
const ON_HOUR = new Intl.DateTimeFormat(undefined, { hour: "2-digit", minute: "2-digit", hourCycle: "h23" });
const ON_DATE = new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", year: "numeric" });
const MUTED = "text-[color:var(--muted)]";
const INK = "text-[color:var(--ink)]";
const PENDING = "inline-block max-w-full animate-pulse rounded-[2px] bg-[color:var(--line-soft)] motion-reduce:animate-none";
// A row is as tall as the room allows (--ov-row, fitRoom's `row`).
const ROW = "grid h-[var(--ov-row)] w-full items-center border-t border-[color:var(--line-soft)] text-left text-[13px] leading-none";
const DOOR = "hover:bg-[color:var(--ov-row-on)] focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[color:var(--green)]";
const HEAD = "grid h-[28px] items-center border-b border-[color:var(--line)] text-[12px] leading-none text-[color:var(--muted)] [&+*]:border-t-0";
// The agents table: mark, name, bar, traces, share, change; on a narrower
// page, no share; on a phone (the page under 740px), no bar either.
const AGENT_COLS = "grid-cols-[26px_196px_minmax(0,1fr)_80px_64px_92px] @max-[980px]:grid-cols-[26px_160px_minmax(0,1fr)_72px_84px] @max-[740px]:grid-cols-[26px_minmax(0,1fr)_76px_84px]";
const RISK_COLS = "grid-cols-[76px_minmax(0,1fr)_64px_76px]";
const WIDE = "@max-[740px]:hidden";
const SHARE = "@max-[980px]:hidden";
// The room's blocks in px, as the classes here draw them, for fitRoom: side
// by side, and stacked on a phone.
// The share chart needs height to read: on a laptop it keeps 260px, and
// 220px at the least, before the agents table and the cards give way.
const DESK = { head: 28, row: [28, 37, 22], chart: [260, 220], chartGap: 18, gridGap: 18, sevGap: 0, cardsGap: 14, card: 96 };
const PHONE = { ...DESK, chart: [96, 64], chartGap: 12, gridGap: 12, sevGap: 16, cardsGap: 8, stacked: true };
// Export report draws the page as the design does on the printed page
// (OverviewPage.css: 1200px by 900px, less the page's insets): every row,
// and the chart 262px tall.
const PRINT = { width: 1180, fit: { row: 37, slots: Infinity, chart: 262, cards: true } };
const plural = (n, one, many = `${one}s`) => `${fmt(n)} ${n === 1 ? one : many}`;
const stateOf = (q) => (q.data != null ? "ready" : q.loading ? "loading" : "failed");

// A placeholder for a number still being read: a quiet bar, nothing faked.
function Pending({ w = 48, h = 12 }) {
  return <span aria-hidden="true" className={PENDING} style={{ width: w, height: h }} />;
}

// Hover or focus names what is under it in words.
function Readout({ label, placement = "top", children }) {
  return <HoverTooltip wide placement={placement} label={label}>{children}</HoverTooltip>;
}

// Where the page's rows lead, the range going along: Fleet's agents (one
// found by name), Findings at a severity or searched (Fleet's rows), the
// investigations.
const LEAVE = ["adminView", "settingsView", "setting", "view", "menu", "tab", "tabs", "scope", "active", "fleetView", "fleetSearch", "fleetSelected"];
export function useDoors() {
  const [, setParams] = useSearchParams();
  const go = (mode, extra = {}) => setParams((p) => {
    const next = new URLSearchParams(p);
    for (const k of LEAVE) next.delete(k);
    next.set("mode", mode);
    if (mode === "triage" || mode === "endpoints") next.set("adminView", mode);
    for (const [k, v] of Object.entries(extra)) next.set(k, v);
    return next;
  });
  return {
    fleet: (name = null) => { setFleet({ view: "agents" }); go("endpoints", name ? { fleetSearch: name } : {}); },
    // Findings has quick filters for critical and high; a lesser severity opens it most severe first.
    findings: (level = null) => { setConsole(level === "critical" || level === "high" ? { quick: level } : { quick: "all", sort: "severity" }); go("triage"); },
    findingsFor: (query) => { setConsole({ quick: "all", query }); go("triage"); },
    investigations: () => go("workspace"),
  };
}

// Whether the page is being printed: it is drawn for the printed page before
// the browser lays that out, so synchronously, then for the window again.
function usePrinting() {
  const [printing, setPrinting] = useState(false);
  useEffect(() => {
    const before = () => flushSync(() => setPrinting(true));
    const after = () => setPrinting(false);
    window.addEventListener("beforeprint", before);
    window.addEventListener("afterprint", after);
    return () => {
      window.removeEventListener("beforeprint", before);
      window.removeEventListener("afterprint", after);
    };
  }, []);
  return printing;
}

// A count's bar on its panel's scale (`max`), with the span before's as a
// pale ghost and a tick where it ended.
export function Pair({ now, before, max, fill, className = "" }) {
  const g = before ? (before / max) * 100 : 0;
  const b = now ? Math.max(2, (now / max) * 100) : 0;
  return (
    <span className={`relative h-[10px] ${className}`} aria-hidden="true">
      {before ? <i className="absolute inset-y-0 left-0 rounded-[1px] bg-[color:var(--ov-ghost)]" style={{ width: `${g}%` }} /> : null}
      <i className={`absolute inset-y-0 left-0 rounded-[1px] ${fill ? "" : "bg-[color:var(--ov-pale)] group-hover:bg-[color:var(--ov-pale-on)]"}`} style={{ width: `${b}%`, ...(fill ? { background: fill } : {}) }} />
      {before ? <i className="absolute -inset-y-[3px] w-[1.5px] bg-[color:var(--ov-tick)]" style={{ left: `calc(${g}% - 0.75px)` }} /> : null}
    </span>
  );
}

// A tile: a label, a figure and its change, and the span before's count.
// The middle one is ruled off on each side; on a phone they stay three
// across, smaller, the change under the figure and the count before left to
// the readout.
const TILE_PLACE = [
  "pl-0 pr-[24px] [--ov-l:-8px] @max-[740px]:pr-[12px]",
  "px-[24px] border-l border-[color:var(--line)] @max-[740px]:px-[12px]",
  "pl-[24px] pr-0 border-l border-[color:var(--line)] [--ov-r:-8px] @max-[740px]:pl-[12px]",
];
export function Tile({ at, label, state, figure, change, before, tip, onOpen = null, name }) {
  const Box = onOpen ? "button" : "div";
  return (
    <Readout label={state === "ready" ? tip : null} placement="bottom">
      <Box
        {...(onOpen ? { type: "button", onClick: onOpen } : { tabIndex: 0 })}
        data-ov-mark=""
        data-overview-tile={name}
        className={`group relative block min-w-0 py-[16px] text-left outline-none [--ov-t:8px] [--ov-b:8px] [--ov-l:16px] [--ov-r:16px] ${TILE_PLACE[at]} @max-[740px]:py-[10px]`}
      >
        <span className={`block whitespace-nowrap text-[13px] font-medium leading-[16px] ${MUTED} group-hover:text-[color:var(--ink)] @max-[740px]:text-[12px]`}>{label}</span>
        <span className="mt-[6px] flex min-h-[34px] items-baseline gap-[10px] @max-[740px]:mt-[2px] @max-[740px]:min-h-[44px] @max-[740px]:flex-col @max-[740px]:items-start @max-[740px]:gap-0">
          {state === "ready" ? (
            <>
              <span className={`fade-in text-[28px] font-medium leading-[34px] tracking-[-0.02em] tabular-nums ${INK} @max-[740px]:text-[22px] @max-[740px]:leading-[28px]`}>{figure}</span>
              {change ? <span className={`whitespace-nowrap text-[13px] font-medium leading-none tabular-nums ${INK} @max-[740px]:text-[12px] @max-[740px]:leading-[16px]`}>{change}</span> : null}
            </>
          ) : state === "failed" ? <span className={`text-[13px] leading-[34px] ${MUTED}`}>Unavailable</span> : <span className="flex h-[34px] w-full items-center @max-[740px]:h-[28px]"><Pending w={104} h={22} /></span>}
        </span>
        <span className={`mt-[2px] block min-h-[16px] text-[12px] leading-[16px] tabular-nums ${MUTED} @max-[740px]:hidden`}>{state === "ready" ? before : null}</span>
      </Box>
    </Readout>
  );
}

// The running total: traces so far, unit by unit across the range, over the
// span before's as a pale ghost, each line's end labelled, `W` by `H` px.
// Hover or focus a unit for its words; the arrow keys step along.
const CHART_TOP = 14;
const AXIS = 26;
export function RunningTotal({ state, series, unit, num, compare, len, width: W, height: H }) {
  const box = useRef(null);
  const [at, setAt] = useState(null);
  const now = series?.now || [];
  const N = now.length;
  const ghost = compare && series?.before ? series.before.slice(0, N) : null;
  const base = H - AXIS;
  const end = now[N - 1]?.run ?? 0;
  const endBefore = ghost?.length ? ghost[ghost.length - 1].run : null;
  const ymax = Math.max(end, endBefore ?? 0, 1) * 1.1;
  const y = (v) => base - (v / ymax) * (base - CHART_TOP);
  const xe = (i) => ((i + 1) / N) * W;
  const line = (pts) => `M0 ${y(0)} ${pts.map((p, i) => `L${xe(i)} ${y(p.run)}`).join(" ")}`;
  const drawn = state === "ready" && W > 0 && N > 0 && end > 0;
  // The two ends' labels stand above their points, the lower under it where
  // it would meet the other line, as on a short chart.
  const close = endBefore != null && Math.abs(y(end) - y(endBefore)) < 24;
  const labelY = (v, lower) => (close && lower ? y(v) + 18 : y(v) - 9);
  const ticks = now.map((u, i) => (unit === "hour"
    ? (new Date(u.at).getHours() % 6 === 0 ? [i, ON_HOUR.format(u.at)] : null)
    : (N <= 10 || new Date(u.at).getDay() === 1 ? [i, ON_DAY.format(u.at)] : null))).filter(Boolean);
  const words = (i) => {
    const u = now[i];
    const g = ghost?.[i];
    const when = unit === "hour" ? `${ON_DAY.format(u.at)}, ${ON_HOUR.format(u.at)} to ${ON_HOUR.format(u.at + HOUR)}` : `${ON_WEEKDAY.format(u.at)}, ${ON_DAY.format(u.at)}`;
    return (
      <>
        By the end of {when}: <b>{plural(u.run, "trace")}</b>, {fmt(u.n)} of them that {unit}.
        {g ? <><br />By {unit === "hour" ? `${ON_HOUR.format(g.at + HOUR)} on ${ON_DAY.format(g.at)}` : ON_DAY.format(g.at)}, in the {len} before: {fmt(g.run)}.</> : null}
      </>
    );
  };
  const pick = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    setAt(Math.max(0, Math.min(N - 1, Math.floor(((e.clientX - r.left) / r.width) * N))));
  };
  const key = (e) => {
    if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
    e.preventDefault();
    setAt((i) => Math.max(0, Math.min(N - 1, (i ?? N - 1) + (e.key === "ArrowLeft" ? -1 : 1))));
  };
  const rect = drawn && at != null ? (() => {
    const r = box.current.getBoundingClientRect();
    const x = r.left + xe(at);
    const top = r.top + y(now[at].run);
    return { left: x, right: x, top, bottom: top, width: 0, height: 0 };
  })() : null;
  return (
    <div
      ref={box}
      className="relative mt-[18px] shrink-0 outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--green)] @max-[740px]:mt-[12px]"
      style={{ height: H }}
      tabIndex={drawn ? 0 : undefined}
      role={drawn ? "img" : undefined}
      aria-label={drawn ? `Traces so far: ${fmt(end)}${endBefore != null ? `, against ${fmt(endBefore)} in the ${len} before` : ""}` : undefined}
      onMouseMove={drawn ? pick : undefined}
      onMouseLeave={() => setAt(null)}
      onFocus={() => drawn && setAt(N - 1)}
      onBlur={() => setAt(null)}
      onKeyDown={drawn ? key : undefined}
      data-overview-chart=""
    >
      {drawn ? (
        <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} className="fade-in block h-auto w-full overflow-visible" aria-hidden="true">
          <path d={`${line(now)} L${W} ${base} L0 ${base} Z`} fill="var(--ov-trace-fill)" />
          {ghost ? <path d={line(ghost)} fill="none" stroke="var(--ov-ghost-line)" strokeWidth="1.5" strokeLinejoin="round" /> : null}
          <path d={line(now)} fill="none" stroke="var(--ov-trace)" strokeWidth="1.75" strokeLinejoin="round" />
          <line x1="0" x2={W} y1={base + 0.5} y2={base + 0.5} stroke="var(--line-strong)" strokeWidth="1" />
          {ghost ? <circle cx={W} cy={y(endBefore)} r="2.5" fill="var(--bg)" stroke="var(--ov-ghost-line)" strokeWidth="1.5" /> : null}
          <circle cx={W} cy={y(end)} r="3" fill="var(--ov-trace)" />
          {at != null ? (
            <g>
              <line x1={xe(at)} x2={xe(at)} y1={CHART_TOP} y2={base} stroke="var(--line-strong)" />
              {ghost?.[at] ? <circle cx={xe(at)} cy={y(ghost[at].run)} r="3" fill="var(--bg)" stroke="var(--ov-ghost-line)" strokeWidth="1.5" /> : null}
              <circle cx={xe(at)} cy={y(now[at].run)} r="3.5" fill="var(--ov-trace)" />
            </g>
          ) : null}
          <text x={W} y={labelY(end, endBefore != null && end < endBefore)} textAnchor="end" className="fill-[color:var(--ink)] text-[12px] font-medium tabular-nums">{num(end)}</text>
          {ghost ? <text x={W} y={labelY(endBefore, endBefore <= end)} textAnchor="end" className="fill-[color:var(--muted)] text-[12px] tabular-nums">{num(endBefore)}</text> : null}
          {/* A label that would run off the right edge ends at it. */}
          {ticks.map(([i, label]) => <text key={i} x={(i / N) * W > W - 48 ? W : (i / N) * W} y={H - 6} textAnchor={(i / N) * W > W - 48 ? "end" : "start"} className="fill-[color:var(--muted)] text-[12px] tabular-nums">{label}</text>)}
        </svg>
      ) : (
        <div className={`absolute inset-x-0 top-[14px] grid place-items-center border-b border-[color:var(--line-strong)] text-[13px] ${MUTED}`} style={{ height: base - CHART_TOP }}>
          {state === "failed" ? "Unavailable" : state === "ready" ? "No traces in this range" : null}
        </div>
      )}
      {/* Mounted only while a unit is read: the readout goes with it. */}
      {rect ? <HoverTooltip wide placement="top" anchorRect={rect} label={words(at)} /> : null}
    </div>
  );
}

// The share chart: of each step's window of traces (the 7 days to a day, or
// the 24 hours to an hour at two days or less), the share each of the agents
// table's rows took, `width` by `H` px. Each agent wears its row's hue, so the
// table is the chart's key; the new are sage and the rest grey. The bands are
// named at the right with their first share and their last. Pointing at a
// band, its label or its row lights it and dims the others.
const GUTTER = 196;
const LABEL_GAP = 18;
const pct = (v) => (v > 0 && v < 0.005 ? "<1%" : `${Math.round(v * 100)}%`);
const listed = (names, n = 3) => (names.length > n ? `${names.slice(0, n).join(", ")} and ${fmt(names.length - n)} more` : names.join(", ").replace(/, ([^,]*)$/, " and $1"));
// A row's hue: the named agents in rank order (TOP of them at most), then the new and the rest.
const rowFill = (key, k) => (key === "new" ? "var(--green)" : key === "more" ? "var(--ov-more)" : `var(--ov-hue-${k + 1})`);

// Labels at their bands' ends, pushed apart to LABEL_GAP and held above `hi`.
function spread(items, hi) {
  items.sort((a, b) => a.y - b.y);
  for (let i = 1; i < items.length; i += 1) items[i].y = Math.max(items[i].y, items[i - 1].y + LABEL_GAP);
  const over = items.length ? items[items.length - 1].y - hi : 0;
  if (over > 0) {
    items[items.length - 1].y = hi;
    for (let i = items.length - 2; i >= 0; i -= 1) items[i].y = Math.min(items[i].y, items[i + 1].y - LABEL_GAP);
  }
  return items;
}

function AgentShare({ state, shares, rows, unit, lit, onLit, width, height: H }) {
  const box = useRef(null);
  const [at, setAt] = useState(null);
  const [py, setPy] = useState(null);
  const gutter = width >= 740 ? GUTTER : 0;
  const W = Math.max(0, width - gutter);
  const N = shares?.steps.length ?? 0;
  const base = H - AXIS;
  // Traces only in the week before the range are no traces in it.
  const drawn = state === "ready" && W > 0 && N > 1 && shares.now > 0;
  const row = (key) => rows?.find((r) => r.key === key);
  let floor = shares?.steps.map(() => 0) ?? [];
  const bands = drawn ? shares.bands.map((b, k) => { const lower = floor; floor = lower.map((l, i) => l + b.share[i]); return { ...b, k, name: row(b.key)?.name ?? "Others", lower, upper: floor }; }) : [];
  const x = (i) => (i / (N - 1)) * W;
  const y = (v) => base - v * (base - CHART_TOP);
  const focus = bands.some((b) => b.key === lit) ? lit : null;
  const area = (b) => `M${b.upper.map((v, i) => `${x(i)} ${y(v)}`).join(" L")} L${b.lower.map((v, i) => `${x(i)} ${y(v)}`).reverse().join(" L")} Z`;
  // As many labels as the height holds, the largest shares first.
  const room = Math.floor((base - 4 - CHART_TOP) / LABEL_GAP) + 1;
  const named = gutter ? (bands.length <= room ? bands : [...bands].sort((a, b) => b.to - a.to).slice(0, room)) : [];
  const labels = spread(named.map((b) => { const mid = y((b.lower[N - 1] + b.upper[N - 1]) / 2); return { b, mid, y: mid }; }), base - 4);
  const ticks = drawn ? shares.steps.map((t, i) => (unit === "hour"
    ? (new Date(t).getHours() % 6 === 0 ? [i, ON_HOUR.format(t)] : null)
    : (N <= 10 || new Date(t).getDay() === 1 ? [i, ON_DAY.format(t)] : null))).filter(Boolean) : [];
  const words = (i) => {
    const end = shares.steps[i];
    const fresh = row("new")?.agents;
    return (
      <>
        <b>The {SHARE_WINDOW[unit]} {unit}s to {unit === "hour" ? `${ON_HOUR.format(end + HOUR)} on ${ON_DAY.format(end)}` : `${ON_WEEKDAY.format(end)}, ${ON_DAY.format(end)}`}</b>: {plural(shares.totals[i], "trace")}
        <span className="mt-[4px] grid grid-cols-[minmax(0,1fr)_auto] gap-x-[16px] tabular-nums">
          {bands.map((b) => <Fragment key={b.key}><span className={b.key === focus ? "font-semibold" : ""}>{b.name}</span><span className="text-right">{pct(b.share[i])}</span></Fragment>)}
        </span>
        {focus === "new" && fresh?.length ? <span className="mt-[6px] block opacity-75">New: {listed(fresh.map((a) => a.name))}</span> : null}
      </>
    );
  };
  const pick = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const mx = e.clientX - r.left;
    const my = e.clientY - r.top;
    // Over the gutter, the label under the pointer is the one looked at.
    if (mx > W) {
      setAt(null);
      onLit(e.target.closest?.("[data-overview-share-label]")?.getAttribute("data-overview-share-label") ?? null);
      return;
    }
    const i = Math.max(0, Math.min(N - 1, Math.round((mx / W) * (N - 1))));
    const v = (base - my) / (base - CHART_TOP);
    setAt(i);
    setPy(my);
    onLit(bands.find((b) => v >= b.lower[i] && v <= b.upper[i])?.key ?? null);
  };
  const key = (e) => {
    if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
    e.preventDefault();
    setAt((i) => Math.max(0, Math.min(N - 1, (i ?? N - 1) + (e.key === "ArrowLeft" ? -1 : 1))));
  };
  const leave = () => { setAt(null); setPy(null); onLit(null); };
  const rect = drawn && at != null ? (() => {
    const r = box.current.getBoundingClientRect();
    const px = r.left + x(at);
    const top = r.top + (py ?? CHART_TOP);
    return { left: px, right: px, top, bottom: top, width: 0, height: 0 };
  })() : null;
  return (
    <div
      ref={box}
      className="relative mt-[18px] shrink-0 outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--green)] @max-[740px]:mt-[12px]"
      style={{ height: H }}
      tabIndex={drawn ? 0 : undefined}
      role={drawn ? "img" : undefined}
      aria-label={drawn ? `Each agent's share of traces, first to last: ${bands.map((b) => `${b.name} ${pct(b.from)} to ${pct(b.to)}`).join(", ")}` : undefined}
      onMouseMove={drawn ? pick : undefined}
      onMouseLeave={leave}
      onFocus={() => drawn && setAt(N - 1)}
      onBlur={leave}
      onKeyDown={drawn ? key : undefined}
      data-overview-chart=""
    >
      {drawn ? (
        <>
          <svg width={width} height={H} viewBox={`0 0 ${width} ${H}`} className="fade-in block h-auto w-full overflow-visible" aria-hidden="true">
            {bands.map((b) => <path key={b.key} d={area(b)} fill={rowFill(b.key, b.k)} fillOpacity={focus && focus !== b.key ? 0.3 : 1} stroke="var(--bg)" strokeWidth="1.25" strokeLinejoin="round" data-overview-share-band={b.key} />)}
            <line x1="0" x2={W} y1={base + 0.5} y2={base + 0.5} stroke="var(--line-strong)" strokeWidth="1" />
            {at != null ? <line x1={x(at)} x2={x(at)} y1={CHART_TOP} y2={base} stroke="var(--ink)" strokeOpacity="0.5" /> : null}
            {labels.map((l) => <line key={l.b.key} x1={W + 3} x2={W + 9} y1={l.mid} y2={l.y} stroke="var(--line-strong)" />)}
            {/* A label that would run off the right edge ends at it. */}
            {ticks.map(([i, label]) => <text key={i} x={x(i) > W - 48 ? W : x(i)} y={H - 6} textAnchor={x(i) > W - 48 ? "end" : "start"} className="fill-[color:var(--muted)] text-[12px] tabular-nums">{label}</text>)}
          </svg>
          {labels.map(({ b, y: ly }) => (
            <span key={b.key} className="absolute flex h-[16px] items-center gap-[6px] text-[12px] leading-[16px]" style={{ left: W + 13, top: ly - 8, width: gutter - 13 }} data-overview-share-label={b.key}>
              <span className={`flex w-[14px] shrink-0 ${INK}`}>{b.key !== "new" && b.key !== "more" ? <AgentIcon name={b.name} className="h-[14px] w-[14px]" /> : null}</span>
              <span className={`min-w-0 truncate ${b.key === "more" ? MUTED : INK} ${b.key === focus ? "font-semibold" : ""}`}>{b.name}</span>
              <span className={`ml-auto shrink-0 tabular-nums ${MUTED}`}>{pct(b.from)} to {pct(b.to)}</span>
            </span>
          ))}
        </>
      ) : (
        <div className={`absolute inset-x-0 top-[14px] grid place-items-center border-b border-[color:var(--line-strong)] text-[13px] ${MUTED}`} style={{ height: base - CHART_TOP }}>
          {state === "failed" ? "Unavailable" : state === "ready" ? "No traces in this range" : null}
        </div>
      )}
      {/* Mounted only while a step is read: the readout goes with it. */}
      {rect ? <HoverTooltip wide placement="top" anchorRect={rect} label={words(at)} /> : null}
    </div>
  );
}

// The "N new" row's marks, each naming its agent on hover in the row's readout.
const MARKS = 5;

function AgentsTable({ state, table, counts, compare, num, span, beforeSpan, len, doors, hold, onLit }) {
  const [sub, setSub] = useState(null);
  const share = (t) => `${(t / 10).toFixed(1)}%`;
  const against = (before) => (compare ? `, against ${fmt(before)} in the ${len} before (the tick)` : "");
  if (state !== "ready") {
    // While it is read, it holds the rows kept for it (`hold`), so nothing moves when it lands.
    return (
      <section className="min-w-0 pr-[24px] @max-[980px]:pr-[16px] @max-[740px]:pr-0" style={{ minHeight: `calc(28px + ${hold} * var(--ov-row))` }} data-overview-agents="">
        <div className={`${HEAD} ${AGENT_COLS}`}><span /><span>Agent</span><span className={WIDE} /><span className="text-right">Traces</span><span className={`text-right ${SHARE}`}>Share</span><span className="text-right">Change</span></div>
        <div className={`flex h-[var(--ov-row)] items-center text-[13px] ${MUTED}`}>{state === "failed" ? "Unavailable" : <Pending w={160} />}</div>
      </section>
    );
  }
  const { lead, fresh, others, gone, total, max } = table;
  return (
    <section className="min-w-0 pr-[24px] @max-[980px]:pr-[16px] @max-[740px]:pr-0" data-overview-agents="">
      <div className={`${HEAD} ${AGENT_COLS}`}><span /><span>Agent</span><span className={WIDE} /><span className="text-right">Traces</span><span className={`text-right ${SHARE}`}>Share</span><span className="text-right">Change</span></div>
      {lead.map((a, k) => (
        <Readout key={a.key} label={<><b>{a.name}</b>{a.isNew ? ", new" : ""}: {plural(a.now, "trace")}, {share(a.share)} of all{against(a.before)}.</>}>
          <button
            type="button"
            onClick={() => doors.fleet(a.name)}
            onMouseEnter={() => onLit(a.key)}
            onMouseLeave={() => onLit(null)}
            onFocus={() => onLit(a.key)}
            onBlur={() => onLit(null)}
            className={`group ${ROW} ${AGENT_COLS} ${DOOR}`}
            data-overview-agent={a.key}
          >
            <span className={`flex items-center ${INK}`}><AgentIcon name={a.name} className="h-[16px] w-[16px]" /></span>
            <span className="truncate pr-[12px]">{a.name}{a.isNew ? <span className="ml-[8px] text-[12px] font-semibold">new</span> : null}</span>
            <Pair now={a.now} before={a.before} max={max} fill={rowFill(a.key, k)} className={`mr-[28px] @max-[980px]:mr-[16px] ${WIDE}`} />
            <span className="text-right font-medium tabular-nums">{num(a.now)}</span>
            <span className={`text-right tabular-nums ${MUTED} ${SHARE}`}>{share(a.share)}</span>
            <span className={`text-right tabular-nums ${MUTED}`}>{moveText(a.now, a.before, num)}</span>
          </button>
        </Readout>
      ))}
      {fresh.agents.length ? (
        <Readout label={sub ? <><b>{sub.name}</b>, new: {plural(sub.now, "trace")}.</> : <><b>{plural(fresh.agents.length, "new agent")}</b> beyond the {lead.length} above: {plural(fresh.now, "trace")} from {span}, none in the {len} before.</>}>
          <button type="button" onClick={() => doors.fleet()} onMouseEnter={() => onLit("new")} onMouseLeave={() => onLit(null)} className={`group ${ROW} ${AGENT_COLS} ${DOOR}`} data-overview-fresh="">
            <span />
            <span className="truncate pr-[12px]">{fmt(fresh.agents.length)}<span className="ml-[5px] text-[12px] font-semibold">new</span></span>
            {/* As many whole marks as the column holds: the rest wrap out of sight. */}
            <span className={`flex h-[16px] flex-wrap items-center gap-x-[10px] overflow-hidden ${INK} ${WIDE}`}>
              {fresh.agents.slice(0, MARKS).map((a) => (
                <span key={a.key} onMouseEnter={() => setSub(a)} onMouseLeave={() => setSub(null)} className="flex"><AgentIcon name={a.name} className="h-[16px] w-[16px]" /></span>
              ))}
              {fresh.agents.length > MARKS ? <span className={`text-[12px] leading-[16px] tabular-nums ${MUTED}`}>+{fmt(fresh.agents.length - MARKS)}</span> : null}
            </span>
            <span className="text-right font-medium tabular-nums">{num(fresh.now)}</span>
            <span className={`text-right tabular-nums ${MUTED} ${SHARE}`}>{share(fresh.share)}</span>
            <span className={`text-right tabular-nums ${MUTED}`}>{compare ? moveText(fresh.now, 0, num) : ""}</span>
          </button>
        </Readout>
      ) : null}
      {others.count ? (
        <Readout label={<><b>{plural(others.count, "more agent")}</b>: {plural(others.now, "trace")}{compare ? `, against ${fmt(others.before)} in the ${len} before` : ""}{gone.before ? `, ${fmt(gone.before)} of them from ${plural(gone.count, "agent")} not seen since` : ""}.</>}>
          <button type="button" onClick={() => doors.fleet()} onMouseEnter={() => onLit("more")} onMouseLeave={() => onLit(null)} className={`group ${ROW} ${AGENT_COLS} ${DOOR}`} data-overview-more="">
            <span />
            <span className={`truncate pr-[12px] ${MUTED}`}>{fmt(others.count)} more</span>
            <Pair now={others.now} before={others.before} max={max} fill="var(--ov-more)" className={`mr-[28px] @max-[980px]:mr-[16px] ${WIDE}`} />
            <span className="text-right font-medium tabular-nums">{num(others.now)}</span>
            <span className={`text-right tabular-nums ${MUTED} ${SHARE}`}>{share(others.share)}</span>
            <span className={`text-right tabular-nums ${MUTED}`}>{moveText(others.now, others.before, num)}</span>
          </button>
        </Readout>
      ) : null}
      <Readout label={<><b>{plural(counts.now, "agent")}</b>, {plural(total.now, "trace")} from {span}{compare ? `, against ${fmt(total.before)} from ${beforeSpan}` : ""}.</>}>
        <div tabIndex={0} className={`${ROW} ${AGENT_COLS} border-t-[color:var(--line-strong)] font-medium outline-none focus-visible:bg-[color:var(--ov-row-on)]`} data-overview-total="">
          <span />
          <span>Total</span>
          <span className={WIDE} />
          <span className="text-right font-semibold tabular-nums">{num(total.now)}</span>
          <span className={`text-right tabular-nums ${MUTED} ${SHARE}`}>{total.now ? "100%" : ""}</span>
          <span className={`text-right tabular-nums ${INK}`}>{moveText(total.now, total.before, num)}</span>
        </div>
      </Readout>
    </section>
  );
}

const LEVEL_FILL = { critical: "var(--ov-critical)", high: "var(--ov-high)", medium: "var(--ov-medium)", low: "var(--ov-low)" };

// Each severity on one scale, with the span before's tick; then the four
// together, then the traces no review has reached yet.
function SeverityRows({ state, now, before, num, span, len, doors }) {
  const compare = !!before;
  const max = state === "ready" ? Math.max(1, ...LEVELS.flatMap((l) => [now[l], before?.[l] ?? 0])) : 1;
  const value = (v, cls = "") => (state === "ready" ? <span className={`text-right tabular-nums ${cls}`}>{num(v, now.flagged)}</span> : <span className="flex justify-end"><Pending w={36} /></span>);
  return (
    <>
      <div className={`${HEAD} ${RISK_COLS}`}><span>Severity</span><span /><span className="text-right">Traces</span><span className="text-right">Change</span></div>
      {state === "failed" ? <div className={`flex h-[var(--ov-row)] items-center text-[13px] ${MUTED}`}>Unavailable</div> : null}
      {state !== "failed" ? LEVELS.map((l) => (
        <Readout key={l} label={state === "ready" ? <><b>{LEVEL_LABEL[l]}: {fmt(now[l])}</b> {now[l] === 1 ? "trace" : "traces"} whose worst risk was {l}{compare ? `, against ${fmt(before[l])} in the ${len} before (the tick)` : ""}.</> : null}>
          <button type="button" onClick={() => doors.findings(l)} className={`group ${ROW} ${RISK_COLS} ${DOOR}`} data-overview-level={l}>
            <span className={INK}>{LEVEL_LABEL[l]}</span>
            {state === "ready" ? <Pair now={now[l]} before={before?.[l]} max={max} fill={LEVEL_FILL[l]} className="mr-[20px]" /> : <span />}
            {value(now?.[l], `font-medium ${l === "critical" ? "text-[color:var(--ov-critical)]" : ""}`)}
            <span className={`text-right tabular-nums ${MUTED}`}>{state === "ready" ? moveText(now[l], before?.[l], (v) => num(v, now.flagged)) : ""}</span>
          </button>
        </Readout>
      )) : null}
      {state !== "failed" ? (
        <>
          <Readout label={state === "ready" ? <><b>{fmt(now.flagged)}</b> traces flagged at any severity{compare ? `, against ${fmt(before.flagged)} in the ${len} before` : ""}. Each trace counts once, at the worst risk its review found.</> : null}>
            <button type="button" onClick={() => doors.findings()} className={`${ROW} ${RISK_COLS} ${DOOR} border-t-[color:var(--line-strong)] font-medium`} data-overview-flagged="">
              <span className="col-span-2">Flagged</span>
              {value(now?.flagged, "font-semibold")}
              <span className={`text-right tabular-nums ${INK}`}>{state === "ready" ? moveText(now.flagged, before?.flagged, num) : ""}</span>
            </button>
          </Readout>
          <Readout label={state === "ready" ? <><b>{fmt(now.unreviewed)}</b> traces from {span} are not reviewed yet, so their worst risk is unknown. They count in no severity above.{compare ? ` ${fmt(before.unreviewed)} from the ${len} before are not reviewed either.` : ""}</> : null}>
            <div tabIndex={0} className={`${ROW} ${RISK_COLS} outline-none focus-visible:bg-[color:var(--ov-row-on)] ${MUTED}`} data-overview-unreviewed="">
              <span className="col-span-2 whitespace-nowrap">Not reviewed yet</span>
              {value(now?.unreviewed, INK)}
              <span />
            </div>
          </Readout>
        </>
      ) : null}
    </>
  );
}

// An investigation card, after an index card: a label, a value, a neutral
// chip and a small mark at its foot. No severity here, so no colour.
function Card({ label, state, value, chip, tip, onOpen, children, name }) {
  return (
    <Readout label={state === "ready" ? tip : null}>
      <button
        type="button"
        onClick={onOpen}
        data-ov-mark=""
        data-overview-card={name}
        className="group relative flex min-w-0 flex-col rounded-[10px] border border-[color:var(--line)] px-[10px] pb-[8px] pt-[7px] text-left outline-none hover:border-[color:var(--line-strong)] focus-visible:border-[color:var(--line-strong)] @max-[980px]:px-[8px]"
      >
        <span className={`whitespace-nowrap text-[12px] leading-[16px] ${MUTED} group-hover:text-[color:var(--ink)]`}>{label}</span>
        {state === "ready" ? (
          <>
            <span className={`fade-in mt-[1px] text-[22px] font-medium leading-[24px] tracking-[-0.01em] tabular-nums ${INK}`}>{value}</span>
            {chip ? <span className={`mt-[3px] self-start whitespace-nowrap rounded-[5px] bg-[color:var(--control-bg)] px-[6px] text-[12px] font-medium leading-[17px] tabular-nums ${INK}`}>{chip}</span> : null}
            {children}
          </>
        ) : state === "failed" ? <span className={`mt-[4px] text-[12px] leading-[16px] ${MUTED}`}>Unavailable</span> : <span className="mt-[5px] flex"><Pending w={40} h={18} /></span>}
      </button>
    </Readout>
  );
}

// Investigations opened step by step, over a dotted line at the span
// before's level; an unfinished last step is dashed and its point open.
function Spark({ steps, level, onStep }) {
  const box = useRef(null);
  const W = Math.floor(useElementSize(box).width);
  const H = 18;
  const pad = 3;
  const max = Math.max(1, level ?? 0, ...steps.map((s) => s.n));
  const gap = (W - 2 * pad) / Math.max(1, steps.length - 1);
  const x = (i) => pad + i * gap;
  const y = (v) => H - (v / max) * (H - 3);
  const pts = steps.map((s, i) => [x(i), y(s.n)]);
  const last = steps.length - 1;
  const open = steps[last]?.partial;
  const path = (ps) => (ps.length ? `M${ps.map((p) => p.join(" ")).join(" L")}` : "");
  return (
    <span
      ref={box}
      className="mt-auto block h-[18px] pt-[0px]"
      onMouseMove={(e) => { const r = e.currentTarget.getBoundingClientRect(); onStep(Math.max(0, Math.min(last, Math.round(((e.clientX - r.left - pad) / Math.max(1, r.width - 2 * pad)) * last)))); }}
      onMouseLeave={() => onStep(null)}
    >
      {W > 0 && steps.length ? (
        <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} className="block h-auto w-full overflow-visible" aria-hidden="true">
          <path d={`${path(pts)} L${x(last)} ${H} L${x(0)} ${H} Z`} fill="var(--ov-trace-fill)" />
          {level != null ? <line x1="0" x2={W} y1={y(level)} y2={y(level)} stroke="var(--ov-ghost-line)" strokeWidth="1" strokeDasharray="1 2.5" strokeLinecap="round" /> : null}
          <path d={path(open ? pts.slice(0, last) : pts)} fill="none" stroke="var(--ov-trace)" strokeWidth="1.5" strokeLinejoin="round" />
          {open && last > 0 ? <path d={path(pts.slice(last - 1))} fill="none" stroke="var(--ov-trace)" strokeWidth="1.5" strokeDasharray="2.5 2" /> : null}
          <circle cx={x(last)} cy={y(steps[last].n)} r="2.25" fill={open ? "var(--bg)" : "var(--ov-trace)"} stroke="var(--ov-trace)" strokeWidth="1.25" />
        </svg>
      ) : null}
    </span>
  );
}

function Cards({ state, inv, compare, span, len, doors }) {
  const [part, setPart] = useState(null);
  const [step, setStep] = useState(null);
  const ready = state === "ready";
  // ponytail: listAllInvestigations reads at most 40 pages of 25 (1,000
  // investigations); past that the cards print the least there are, with
  // "+", and no change. Upgrade: extend POST /v1/finding-investigations:summarize
  // to take a range and return these counts.
  const plus = ready && inv.capped ? "+" : "";
  const invs = (n) => `${fmt(n)}${plus} ${n === 1 && !plus ? "investigation" : "investigations"}`;
  const change = (now, before) => (compare && !plus ? moveText(now, before, fmt) : "");
  const fate = ready ? [
    ["investigating", inv.fate.investigating, "are still being investigated", "var(--ov-trace)"],
    ["waiting", inv.fate.waiting, "are open with nobody holding them", "var(--ov-trace)"],
    ["truePositive", inv.fate.truePositive, "closed as a true positive", "var(--ov-pale-on)"],
    ["joined", inv.fate.joined, "closed by joining another investigation", "var(--ov-pale)"],
    ["closed", inv.fate.closed, "were otherwise closed", "var(--ov-pale)"],
  ].filter(([, n]) => n) : [];
  const stepName = (s) => (inv.step === 7 ? `Week of ${ON_DAY.format(s.from)}${s.partial && s.to < s.from + 7 * 86_400_000 - 1 ? `, to ${ON_DAY.format(s.to)} only` : ""}` : ON_DAY.format(s.from));
  const level = ready && compare && !plus ? inv.levelBefore : null;
  const levelText = level == null ? "" : level >= 100 ? fmt(level) : level.toFixed(1).replace(/\.0$/, "");
  const shown = part ? fate.find(([k]) => k === part) : null;
  return (
    <div className="mt-[14px] grid min-h-[96px] flex-1 grid-cols-3 gap-[8px] @max-[980px]:gap-[6px] @max-[740px]:mt-[8px]" data-overview-cards="">
      <Card
        name="investigating"
        label="Investigating"
        state={state}
        value={ready ? `${fmt(inv.investigating)}${plus}` : null}
        chip={ready && inv.investigating ? `${fmt(inv.people)}${plus} ${inv.people === 1 && !plus ? "person" : "people"}` : null}
        tip={shown ? <><b>{fmt(shown[1])}</b> of the {invs(inv.opened)} opened {shown[2]}.</> : ready ? <><b>{invs(inv.investigating)}</b> still open, of the {fmt(inv.opened)}{plus} opened from {span}. {fmt(inv.people)}{plus} {inv.people === 1 && !plus ? "person holds" : "people hold"} them.</> : null}
        onOpen={doors.investigations}
      >
        {fate.length ? (
          <span className="mt-auto flex h-[4px] gap-[1.5px] pt-0" aria-hidden="true">
            {fate.map(([k, n, , fill]) => <i key={k} className="min-w-[3px] rounded-[1px]" style={{ flex: n, background: fill }} onMouseEnter={() => setPart(k)} onMouseLeave={() => setPart(null)} />)}
          </span>
        ) : null}
      </Card>
      <Card
        name="truePositive"
        label="True positive"
        state={state}
        value={ready ? `${fmt(inv.truePositive)}${plus}` : null}
        chip={ready ? change(inv.truePositive, inv.truePositiveBefore) : null}
        tip={ready ? <><b>{invs(inv.truePositive)}</b> closed as a true positive{compare && !plus ? `, against ${fmt(inv.truePositiveBefore)} in the ${len} before` : ""}. {fmt(inv.falsePositive)}{plus} closed as a false positive{compare && !plus ? `, against ${fmt(inv.falsePositiveBefore)} before` : ""}.</> : null}
        onOpen={doors.investigations}
      />
      <Card
        name="opened"
        label="Opened"
        state={state}
        value={ready ? `${fmt(inv.opened)}${plus}` : null}
        chip={ready ? change(inv.opened, inv.openedBefore) : null}
        tip={ready && step != null && inv.steps[step] ? <>{stepName(inv.steps[step])}: <b>{invs(inv.steps[step].n)}</b> opened.</> : ready ? <><b>{invs(inv.opened)}</b> opened from {span}{level != null ? `, against ${fmt(inv.openedBefore)} in the ${len} before: ${levelText} a ${inv.step === 7 ? "week" : "day"}, the dotted line` : ""}.</> : null}
        onOpen={doors.investigations}
      >
        {/* A trend needs two steps; a day or less has one. */}
        {inv?.steps.length > 1 ? <Spark steps={inv.steps} level={level} onStep={setStep} /> : null}
      </Card>
    </div>
  );
}

export default function OverviewPage({ range, onRangeChange }) {
  const w = windowsOfRange(range);
  const caps = useMyPermissions();
  const user = useCurrentUser();
  const doors = useDoors();
  const people = hasPermission(caps, PERMISSIONS.DIRECTORY_DATA_READ);
  const traces = useQuery("getOverviewTraces", { days: w.days });
  const agents = useQuery("getOverviewAgents", { now: w.now, before: w.before });
  const byDay = useQuery("getOverviewPairsByDay", { span: shareSpan(w), unit: w.unit });
  const severity = useQuery("getOverviewSeverity", { days: w.days });
  const reach = useQuery("getOverviewReach", { now: w.now, people });
  const enrolled = useQuery("getInventoryTotal", { range: "1M" }, { enabled: hasPermission(caps, PERMISSIONS.ENDPOINT_METADATA_READ) });
  const investigations = useConsoleInvestigations();

  // The words the page says of its spans: the range's dates, the span
  // before's, and how long it is ("30 days", "24 hours").
  const span = spanWords(w.now.from, w.now.to);
  const beforeSpan = w.before ? spanWords(w.before.from, w.before.to) : "";
  const len = lengthOf(w.now.from, w.now.to - 59_999);

  const tracesState = stateOf(traces);
  const tNow = tracesIn(traces.data, w.now);
  const tBefore = tracesIn(traces.data, w.before);
  // No traces before is no span before: no arrows and no ghost.
  const tCompare = tBefore > 0;
  const num = numberer((tNow ?? 0) >= 1e5);

  const agentsState = stateOf(agents);
  const agentsCompare = !!agents.data?.before && agents.data.before.some((r) => Number(r.traces) > 0);
  const list = agents.data ? agentsOf(agents.data) : null;
  const counts = list ? agentCounts(list, agentsCompare) : null;
  // The band looked at, in the share chart or by its row in the agents table.
  const [lit, setLit] = useState(null);

  // The room the window leaves the chart and the tables, and how they fit it;
  // unmeasured, or printed, as the design draws them. While the agents are
  // read, the room their table can take at most is kept for them.
  const room = useRef(null);
  const size = useElementSize(room);
  const printing = usePrinting();
  const rows = (slots) => (list ? agentsTable(list, agentsCompare, slots).rows : agentsState === "loading" ? Math.min(slots, TOP + 3) : 1);
  const fit = printing || !size.height ? PRINT.fit : fitRoom(size.height, rows, size.width < 740 ? PHONE : DESK);
  const table = list ? agentsTable(list, agentsCompare, fit.slots) : null;
  // The share chart's bands are the table's rows: its agents, the new, the rest.
  const shareRows = table && byDay.data ? [
    ...table.lead.map((a) => ({ key: a.key, name: a.name, members: [a.key] })),
    ...(table.fresh.agents.length ? [{ key: "new", name: `${fmt(table.fresh.agents.length)} new`, members: table.fresh.agents.map((a) => a.key), agents: table.fresh.agents }] : []),
    { key: "more", name: table.others.count ? `${fmt(table.others.count)} more` : "Others", members: [] },
  ] : null;
  const shareState = stateOf(byDay) === "failed" || agentsState === "failed" ? "failed" : shareRows ? "ready" : "loading";

  const sevState = stateOf(severity);
  const sevNow = severity.data ? severityIn(severity.data, w.now) : null;
  const sevBefore0 = severity.data ? severityIn(severity.data, w.before) : null;
  const sevBefore = sevBefore0?.traces > 0 ? sevBefore0 : null;
  const hc = sevNow ? sevNow.critical + sevNow.high : null;
  const hcBefore = sevBefore ? sevBefore.critical + sevBefore.high : null;

  const invState = investigations.status === "done" ? "ready" : investigations.status === "error" ? "failed" : "loading";
  const inv = invState === "ready" ? { ...investigationsIn(investigations.byId, w), capped: investigations.capped } : null;

  // The newest activity among the range's traces, which the stamp is relative to.
  const newest = Math.max(-Infinity, ...(agents.data?.now || []).map((r) => parseServerTimestamp(r.last)).filter(Number.isFinite));
  const providers = [...new Set((agents.data?.now || []).map((r) => String(r.provider ?? "").trim().toLowerCase()).filter((p) => p && p !== "local"))].map(providerName).sort((a, b) => a.localeCompare(b));
  const tenant = user?.currentTenant?.tenantName || "";

  return (
    <section data-overview="" className="@container flex h-full min-h-0 min-w-0 flex-col overflow-hidden bg-[color:var(--bg)] px-[var(--main-inset)] text-[color:var(--ink)]">
      <header className="flex min-h-[62px] shrink-0 flex-wrap items-center gap-x-[12px] gap-y-[8px] py-[8px]">
        <h1 className="m-0 text-[22px] font-semibold leading-[1.15] tracking-[-0.02em]">Overview</h1>
        <span className={`text-[13px] tabular-nums ${MUTED}`} data-overview-span="">{span}</span>
        <span className={`text-[13px] ${MUTED}`} data-overview-print="">{tenant ? `${tenant}, prepared` : "Prepared"} {ON_DATE.format(Date.now())}</span>
        <span className="flex-1" />
        <Button onClick={() => window.print()} data-print-hide="" data-overview-export="">
          <ArrowDownTrayIcon aria-hidden="true" className="h-[15px] w-[15px]" />
          Export report
        </Button>
        <span className="w-[160px] shrink-0 @max-[740px]:w-[140px]" data-print-hide="">
          <DateRangePicker range={range} onRangeChange={onRangeChange} />
        </span>
      </header>

      <div className="flex min-h-0 flex-1 flex-col pb-[24px] @max-[740px]:pb-[12px]" data-overview-body="">
        <div className="grid grid-cols-3 border-t border-[color:var(--line-strong)]" data-overview-tiles="">
          <Tile
            at={0}
            name="traces"
            label="Traces"
            state={tracesState}
            figure={tNow == null ? "" : num(tNow)}
            change={tCompare ? pctText(tNow, tBefore) : ""}
            before={tCompare ? <><span className="tabular-nums">{num(tBefore)}</span> in the {len} before</> : null}
            tip={<><b>{plural(tNow ?? 0, "trace")}</b> from {span}{tCompare ? `, against ${fmt(tBefore)} from ${beforeSpan}` : ""}.</>}
          />
          <Tile
            at={1}
            name="agents"
            label="Agents"
            state={agentsState}
            figure={counts ? num(counts.now) : ""}
            change={counts && agentsCompare ? `${num(counts.new)} new` : ""}
            before={counts && agentsCompare ? <><span className="tabular-nums">{num(counts.before)}</span> in the {len} before</> : null}
            tip={counts ? <><b>{plural(counts.now, "agent")}</b> produced traces from {span}{agentsCompare ? <>, {fmt(counts.new)} of them new. {fmt(counts.before)} produced traces in the {len} before{counts.gone ? `, and ${fmt(counts.gone)} of those ${counts.gone === 1 ? "has" : "have"} produced none since` : ""}.</> : "."}</> : null}
            onOpen={() => doors.fleet()}
          />
          <Tile
            at={2}
            name="severe"
            label="High or critical"
            state={sevState}
            figure={hc == null ? "" : num(hc)}
            change={sevBefore ? moveText(hc, hcBefore, num) : ""}
            before={sevBefore ? <><span className="tabular-nums">{num(hcBefore)}</span> in the {len} before</> : null}
            tip={sevNow ? <><b>{fmt(hc)}</b> traces whose worst risk was high or critical: {fmt(sevNow.critical)} critical and {fmt(sevNow.high)} high{sevBefore ? `, against ${fmt(hcBefore)} in the ${len} before` : ""}. Each trace counts once, at the worst risk its review found.</> : null}
            onOpen={() => doors.findings()}
          />
        </div>

        <div ref={room} className="flex min-h-0 flex-1 flex-col" style={{ "--ov-row": `${fit.row}px` }} data-overview-room="">
          {fit.chart ? <AgentShare state={shareState} shares={shareRows ? agentShares(byDay.data, agents.data.names?.now, w, shareRows.filter((r) => r.key !== "more")) : null} rows={shareRows} unit={w.unit} lit={lit} onLit={setLit} width={printing ? PRINT.width : Math.floor(size.width)} height={fit.chart} /> : null}

          <div className="mt-[18px] grid grid-cols-[2fr_1fr] @max-[980px]:grid-cols-[minmax(0,1fr)_336px] @max-[740px]:mt-[12px] @max-[740px]:grid-cols-1">
            {fit.slots ? <AgentsTable state={agentsState} table={table} counts={counts} compare={agentsCompare} num={num} span={span} beforeSpan={beforeSpan} len={len} doors={doors} hold={rows(fit.slots)} onLit={setLit} /> : null}
            <section className="flex min-w-0 flex-col border-l border-[color:var(--line)] pl-[24px] @max-[980px]:pl-[16px] @max-[740px]:mt-[16px] @max-[740px]:border-l-0 @max-[740px]:pl-0" data-overview-severity="">
              <SeverityRows state={sevState} now={sevNow} before={sevBefore} num={num} span={span} len={len} doors={doors} />
              {fit.cards ? <Cards state={invState} inv={inv} compare={!!w.before} span={span} len={len} doors={doors} /> : null}
            </section>
          </div>
        </div>

        <Provenance reach={reach} enrolled={enrolled} providers={agents.data ? providers : null} newest={Number.isFinite(newest) ? newest : null} />
      </div>
    </section>
  );
}

// Where the numbers come from: the people and endpoints the traces came
// from, of all there are; the models and the providers that served them;
// and how long ago the newest of the range's traces was active.
function Provenance({ reach, enrolled, providers, newest }) {
  const r = reach.data;
  const e = enrolled.data?.total ?? null;
  const B = ({ children }) => <b className={`font-medium tabular-nums ${INK}`}>{children}</b>;
  const from = r ? (
    <>
      {r.people != null ? <>From <B>{fmt(r.people)}</B>{r.directory != null ? ` of ${fmt(r.directory)}` : ""} people</> : null}
      {r.endpoints != null ? <>{r.people != null ? " on " : "From "}<B>{fmt(r.endpoints)}</B>{e != null ? ` of ${fmt(e)}` : ""} endpoints</> : null}
    </>
  ) : null;
  const fromTip = r ? `Traces came from ${r.people != null ? `${fmt(r.people)}${r.directory != null ? ` of the ${fmt(r.directory)}` : ""} people in the directory, ` : ""}${r.endpoints != null ? `on ${fmt(r.endpoints)}${e != null ? ` of the ${fmt(e)} enrolled` : ""} endpoints` : ""}.` : null;
  const modelsTip = r?.models != null && providers ? `The agents called ${plural(r.models, "model")} from ${plural(providers.length, "provider")}${providers.length ? `: ${providers.join(", ")}` : ""}.` : null;
  return (
    <footer className={`flex flex-wrap items-center gap-x-[28px] gap-y-[6px] pt-[16px] text-[12px] leading-[16px] ${MUTED} @max-[740px]:gap-x-[20px] @max-[740px]:gap-y-[2px] @max-[740px]:pt-[10px]`} data-overview-provenance="">
      {reach.loading && !r ? <Pending w={240} /> : !r || (r.people == null && r.endpoints == null) ? <span>People and endpoints unavailable</span> : (
        <Readout label={fromTip}><span tabIndex={0} className="outline-none focus-visible:text-[color:var(--ink)]">{from}</span></Readout>
      )}
      {r?.models != null || providers ? (
        <Readout label={modelsTip}>
          <span tabIndex={0} className="outline-none focus-visible:text-[color:var(--ink)]">
            {r?.models != null ? <><B>{fmt(r.models)}</B> models</> : null}{r?.models != null && providers ? " from " : null}{providers ? <><B>{fmt(providers.length)}</B> providers</> : null}
          </span>
        </Readout>
      ) : null}
      {newest != null ? <span data-overview-updated="">Updated {formatAgo(new Date(newest))}</span> : null}
    </footer>
  );
}
